import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Check } from "lucide-react";
import { toast } from "sonner";

function BrandSprig() {
  return (
    <svg viewBox="0 0 64 64" className="w-7 h-7 mb-3 opacity-90" aria-hidden="true">
      <path d="M 32 54 C 32 42, 30 34, 25 26" stroke="#FFFFFF" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M 32 46 C 37 46, 40 43, 40 38 C 36 38, 32 41, 32 46 Z" fill="#FFFFFF" />
      <path d="M 30 38 C 25 38, 22 35, 22 30 C 26 30, 30 33, 30 38 Z" fill="#FFFFFF" />
      <path d="M 28 30 C 33 30, 36 27, 36 22 C 32 22, 28 25, 28 30 Z" fill="#FFFFFF" />
      <path d="M 25 26 C 25 21, 28 18, 32 18 C 32 22, 29 26, 25 26 Z" fill="#C27A62" />
    </svg>
  );
}

function ConsentRow({ checked, onChange, label, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="w-full flex items-start gap-3 text-left rounded-xl p-3 hover:bg-[var(--bg-2)] transition-colors"
      data-testid={testid}
    >
      <span
        className="shrink-0 mt-0.5 w-5 h-5 rounded-md flex items-center justify-center"
        style={{
          background: checked ? "var(--brand)" : "transparent",
          border: `1.5px solid ${checked ? "var(--brand)" : "var(--border)"}`,
        }}
      >
        {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
      </span>
      <span className="text-xs leading-relaxed" style={{ color: "var(--text-2)" }}>{label}</span>
    </button>
  );
}


export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consentHealth, setConsentHealth] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        if (!name.trim()) throw new Error("Please enter your name");
        if (password.length < 6) throw new Error("Password must be at least 6 characters");
        if (!consentHealth || !acceptTerms) throw new Error("Please tick both consent boxes to continue");
        await register(name, email, password, {
          consent_health_data: consentHealth,
          accepted_terms: acceptTerms,
        });
        toast.success("Account created");
      }
      nav("/questionnaire");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative" style={{ background: "var(--bg-2)" }}>
        <img
          src="https://images.unsplash.com/photo-1767611125208-4f711306b13f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxjYWxtJTIwd2VsbG5lc3MlMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzgxMDI1NDY5fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A332B]/40 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <BrandSprig />
          <p className="font-serif text-3xl leading-tight max-w-md">
            A calmer relationship with your wellbeing starts with a thoughtful conversation.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 sm:px-12 py-12">
        <div className="w-full max-w-md fade-up">
          <p className="label-eyebrow mb-3">{mode === "login" ? "Welcome back" : "Begin your path"}</p>
          <h1 className="font-serif text-4xl mb-8 leading-tight">
            {mode === "login" ? "Sign in to Alternavit" : "Create your account"}
          </h1>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="label-eyebrow block mb-2">Name</label>
                <input
                  className="input-soft"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                className="input-soft"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input
                className="input-soft"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                minLength={6}
                data-testid="auth-password-input"
              />
            </div>

            {mode === "register" && (
              <div className="space-y-3 pt-2" data-testid="consent-block">
                <ConsentRow
                  checked={consentHealth}
                  onChange={setConsentHealth}
                  testid="consent-health"
                  label={
                    <>
                      I give my explicit consent to Alternavit processing the wellness information I share
                      (concerns, lifestyle, severity ratings) to generate personalised supplement guidance, in
                      line with GDPR Article 9.
                    </>
                  }
                />
                <ConsentRow
                  checked={acceptTerms}
                  onChange={setAcceptTerms}
                  testid="consent-terms"
                  label={
                    <>
                      I have read and accept the{" "}
                      <Link to="/terms" target="_blank" className="underline" style={{ color: "var(--brand)" }}>
                        Terms
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy" target="_blank" className="underline" style={{ color: "var(--brand)" }}>
                        Privacy Policy
                      </Link>
                      .
                    </>
                  }
                />
              </div>
            )}

            <button type="submit" className="btn-primary w-full mt-2" disabled={loading} data-testid="auth-submit-button">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="mt-8 text-sm" style={{ color: "var(--text-2)" }}>
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button
              className="underline"
              style={{ color: "var(--brand)" }}
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="auth-mode-toggle"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
          {mode === "login" && (
            <p className="mt-2 text-sm">
              <Link to="/forgot-password" className="underline" style={{ color: "var(--text-2)" }} data-testid="forgot-link">
                Forgot your password?
              </Link>
            </p>
          )}
          <p className="mt-3 text-xs">
            <Link to="/" style={{ color: "var(--text-2)" }} className="hover:underline">← Back to home</Link>
          </p>
        </div>
      </div>
    </main>
  );
}
